import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { KEY_PREFIX } from 'marketplace-ui-common/hooks/useSearchParams';
import { useEffect, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect, Route, Switch, generatePath, useLocation, useParams } from 'quartz-routing/react-router';
import { GET_ALL_PUBLISHED_COLLECTIONS_QUERY as GET_ALL_PUBLISHED_COLLECTIONS_QUERY_PUBLIC } from '../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/SelfServePublicRpc.dfc.new';
import { GET_ALL_PUBLISHED_COLLECTIONS_QUERY } from '../../__generated__/chirp/com/hubspot/marketplace/quality/rpc/SelfServeRpc.dfc.new';
import { APPS_FOR_BREEZE_STUDIO, ESSENTIAL_APPS_FOR_SALES } from '../collections/constants/collections';
import { Paths } from '../constants/paths';
import { HomeContainer } from '../containers/Home';
import { RouteReferrerProvider } from '../context/RouteReferrerContext';
import { FeaturedPage } from '../featured/FeaturedPage';
import { useAppContextQuery } from '../hooks/useAppContextQuery';
import { ProductPage } from '../product-home/ProductPage';
import { ExploreSearchResultsProxy } from '../search-results/components/ExploreSearchResultsProxy';
import { ProductsSearchResultsProxy } from '../search-results/components/ProductsSearchResultsProxy';
import { SolutionsPartnersSearchResultsProxy } from '../search-results/components/SolutionsPartnersSearchResultsProxy';
import { SearchResultsPage } from '../search-results/SearchResultsPage';
import { useParsedSearchResultsPathParams } from '../search-results/hooks/useParsedSearchResultsPathParams';
import { SolutionsPartnersPage } from '../solutions/SolutionsPartnersPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LOADING_DELAY_MS = 300;
const DeferredLoadingSpinner = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowSpinner(true), LOADING_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  if (!showSpinner) {
    return null;
  }
  return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    size: "medium",
    marginBlockStart: "1200"
  });
};
const FeaturedCollectionRoute = Loadable({
  loader: () => import( /* webpackChunkName: "FeaturedCollectionRoute" */
  '../featured/FeaturedCollectionRoute').then(mod => mod.FeaturedCollectionRoute),
  LoadingComponent: DeferredLoadingSpinner
});
const InstallSuccess = Loadable({
  loader: () => import( /* webpackChunkName: "InstallSuccess" */
  'marketplace-ui-details/install/InstallSuccess').then(mod => mod.InstallSuccess),
  LoadingComponent: DeferredLoadingSpinner
});
const ThirdPartyAuthState = Loadable({
  loader: () => import( /* webpackChunkName: "ThirdPartyAuthState" */
  'marketplace-ui-details/install/ThirdPartyAuthState').then(mod => mod.ThirdPartyAuthState),
  LoadingComponent: DeferredLoadingSpinner
});
const ListingDetailsContainer = Loadable({
  loader: () => import( /* webpackChunkName: "ListingDetailsRoute" */
  '../containers/ListingDetails').then(mod => mod.ListingDetailsContainer),
  LoadingComponent: DeferredLoadingSpinner
});
const ALL_PATHS = Object.values(Paths);
const InstallSuccessRoute = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "INSTALL_SUCCESS_LOAD",
  children: /*#__PURE__*/_jsx(InstallSuccess, {})
});
const ThirdPartyAuthStateRoute = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "THIRD_PARTY_AUTH_STATE_LOAD",
  children: /*#__PURE__*/_jsx(ThirdPartyAuthState, {})
});

// special handling to preserve query parameters and hash fragments when
// redirecting from legacy write-review URLs to the new listing write-review URLs
function LegacyWriteReviewRedirect() {
  const location = useLocation();
  const {
    listingSlug
  } = useParams();
  const newPath = generatePath(Paths.LISTING_DETAILS_WRITE_REVIEW, {
    listingSlug
  });
  return /*#__PURE__*/_jsx(Redirect, {
    push: false,
    to: {
      pathname: newPath,
      search: location.search,
      hash: location.hash
    }
  });
}
export function FeaturedCollectionPageRedirect() {
  const location = useLocation();
  const {
    collectionSlug,
    page
  } = useParams();
  const newPath = generatePath(Paths.FEATURED_COLLECTION, {
    collectionSlug
  });
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('eco_page', page);
  return /*#__PURE__*/_jsx(Redirect, {
    push: false,
    to: {
      pathname: newPath,
      search: searchParams.toString(),
      hash: location.hash
    }
  });
}
export function Routes({
  referrer
}) {
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsx(Route, {
      path: ALL_PATHS,
      exact: true,
      children: /*#__PURE__*/_jsx(RouteReferrerProvider, {
        referrer: referrer,
        children: /*#__PURE__*/_jsxs(Switch, {
          children: [/*#__PURE__*/_jsx(Route, {
            path: Paths.HOME,
            exact: true,
            component: HomeContainer
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.FEATURED_COLLECTION_PAGE,
            exact: true,
            component: FeaturedCollectionPageRedirect
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.FEATURED_COLLECTION,
            exact: true,
            component: FeaturedCollectionRoute
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.FEATURED, Paths.FEATURED_PAGE],
            exact: true,
            component: FeaturedPage
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.APPS_ALL, Paths.APPS_ALL_PAGE, Paths.APPS_CATEGORY, Paths.APPS_CATEGORY_PAGE],
            exact: true,
            component: AppsSearchResultsRoute
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.APPS,
            exact: true,
            component: AppsProductPage
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.AGENTS_ALL, Paths.AGENTS_ALL_PAGE, Paths.AGENTS_TEAM, Paths.AGENTS_TEAM_PAGE],
            exact: true,
            component: AgentsSearchResultsRoute
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.AGENTS,
            exact: true,
            component: AgentsProductPage
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.SOLUTIONS_PARTNERS,
            exact: true,
            component: SolutionsPartnersPage
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.SOLUTIONS_PARTNERS_WRITE_REVIEW,
            exact: true,
            component: ListingDetailsContainer
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.SOLUTIONS_PARTNERS_ALL, Paths.SOLUTIONS_PARTNERS_ALL_PAGE, Paths.SOLUTIONS_PARTNERS_CATEGORY, Paths.SOLUTIONS_PARTNERS_CATEGORY_SUBCATEGORY, Paths.SOLUTIONS_PARTNERS_CATEGORY_SUBCATEGORY2],
            exact: true,
            component: SolutionsPartnersSearchResultsRoute
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.SEARCH_RESULTS, Paths.SEARCH_RESULTS_PAGE],
            exact: true,
            component: ExploreSearchResultsProxy
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.SEARCH_RESULTS_PRODUCTS, Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS, Paths.SEARCH_RESULTS_WORKFLOW_TEMPLATES],
            exact: true,
            component: SearchResultsPage
          }), /*#__PURE__*/_jsx(Route, {
            path: Paths.LEGACY_DETAILS_WRITE_REVIEW,
            exact: true,
            component: LegacyWriteReviewRedirect
          }), /*#__PURE__*/_jsx(Route, {
            path: [Paths.LISTING_DETAILS, Paths.LISTING_DETAILS_WRITE_REVIEW],
            exact: true,
            component: ListingDetailsContainer
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            path: Paths.LISTING_DETAILS_INSTALL_SUCCESS,
            component: InstallSuccessRoute
          }), /*#__PURE__*/_jsx(Route, {
            exact: true,
            path: Paths.LISTING_DETAILS_THIRD_PARTY_AUTH_STATE,
            component: ThirdPartyAuthStateRoute
          }), /*#__PURE__*/_jsx(Redirect, {
            from: "*",
            to: Paths.HOME
          })]
        })
      })
    }), /*#__PURE__*/_jsx(Redirect, {
      from: "/apps",
      to: Paths.APPS
    }), /*#__PURE__*/_jsx(Redirect, {
      from: "/solutions",
      to: Paths.SOLUTIONS_PARTNERS
    }), /*#__PURE__*/_jsx(Redirect, {
      from: "*",
      to: Paths.HOME
    })]
  });
}
export function AppsProductPage() {
  return /*#__PURE__*/_jsx(ProductPage, {
    featuredCollectionId: ESSENTIAL_APPS_FOR_SALES,
    productType: "APP"
  });
}
export function AgentsProductPage() {
  return /*#__PURE__*/_jsx(ProductPage, {
    featuredCollectionId: APPS_FOR_BREEZE_STUDIO,
    productType: "AGENT"
  });
}
export function AppsSearchResultsRoute() {
  var _data$result;
  const {
    categorySlug,
    page
  } = useParsedSearchResultsPathParams();
  const skipCollectionsLookup = !categorySlug || categorySlug === 'featured';
  const {
    data,
    loading,
    error
  } = useAppContextQuery({
    app: GET_ALL_PUBLISHED_COLLECTIONS_QUERY,
    public: GET_ALL_PUBLISHED_COLLECTIONS_QUERY_PUBLIC,
    options: {
      skip: skipCollectionsLookup
    }
  });
  const collectionsRequestState = loading ? 'PENDING' : error ? 'FAILED' : 'SUCCESS';

  /**
   * Redirect legacy app marketplace featured page to the new featured page.
   */
  if (categorySlug === 'featured') {
    return /*#__PURE__*/_jsx(Redirect, {
      to: Paths.FEATURED
    });
  }
  if (collectionsRequestState === 'PENDING') {
    return null;
  }
  const collection = categorySlug ? data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 || (_data$result = _data$result.collections) === null || _data$result === void 0 ? void 0 : _data$result.find(c => c.slug === categorySlug) : undefined;

  /**
   * Redirect legacy app marketplace collection page to the new featured collection page.
   */
  if (collection) {
    const collectionSlug = collection.slug;

    // TODO: Refactor to paginated route
    return /*#__PURE__*/_jsx(Redirect, {
      to: `${generatePath(Paths.FEATURED_COLLECTION, {
        collectionSlug
      })}${page ? `?${KEY_PREFIX}page=${page}` : ''}`
    });
  }
  return /*#__PURE__*/_jsx(ProductsSearchResultsProxy, {
    productType: "APP"
  });
}
export function AgentsSearchResultsRoute() {
  return /*#__PURE__*/_jsx(ProductsSearchResultsProxy, {
    productType: "AGENT"
  });
}
export function SolutionsPartnersSearchResultsRoute() {
  return /*#__PURE__*/_jsx(SolutionsPartnersSearchResultsProxy, {});
}